
import { defineComponent, onMounted, ref, watch } from 'vue';
import BaseButton from '@/components/BaseButton.vue';
import SelectInput from '@/components/SelectInput.vue';
import { getEMSPartnerListApi, getPartnerListApi } from '@/api/QuizApi';
import Multiselect from 'vue-multiselect';
import { getPartnerQuizEmail, sendRequestToPartners } from '@/api/partnerReviewApi';
import useModal from '@/hooks/useModal';
import TheModal from '@/components/TheModal.vue';
import { notification } from 'ant-design-vue';
import { useStore } from 'vuex';
import { key } from '@/store';
import EpxEmsRadio from '@/components/EpxEmsRadio.vue';

export default defineComponent({
  name: 'SendToPartnerPage',
  components: {
    TheModal,
    SelectInput,
    BaseButton,
    Multiselect,
    EpxEmsRadio,
  },
  setup() {
    const store = useStore(key);
    const message = ref(`Hello [partner_name] Below we are requesting the following information from Ciena.

1: To review and edit your questions click the link below.
2: After you are done click confirm and submit.
3: This link will expire in the next 12 hours.
4: Your password to view this link is [password]
5: [url]
`);

    const email = ref('');
    const reminder = ref(null);
    const expires = ref(24);

    const confirmModal = useModal(false);
    const successModal = useModal(false);

    const selectedPartners = ref<{ name: string; id: number } | null>(null);
    const partnersOptions = ref<Array<{ name: string; id: number }>>([]);
    const partnersEMSOptions = ref<Array<{ name: string, id: number }>>([]);
    const selectedEpxEms = ref<string>("epx");

    const epxEmsRadioOptions = [
      { label: "EPX Engagement", value: "epx" },
      { label: "EMS Engagement", value: "ems" },
    ];

    watch(selectedEpxEms, () => {
      selectedPartners.value = null;
    })

    const toggleSidebar = () => {
      store.commit('setSidebarState', !store.state.layout.isSidebarOpen);
    };

    watch(selectedPartners, (newVal) => {
      if (newVal?.id) {
        getPartnerQuizEmail(String(newVal?.id) || '').then(response => {
          email.value = response.email;
        });
      }
    }, { deep: true });

    onMounted(() => {
      getPartnerListApi().then(data => {
        partnersOptions.value = data.map(quiz => ({ id: quiz.id, name: quiz.name }));
      });
      getEMSPartnerListApi().then(data => {
        partnersEMSOptions.value = data.map(quiz => ({id: quiz.id, name: quiz.name}));
      })
    });

    const handleInputChange = (inputValue: string) => {
      message.value = inputValue;
    };

    const onMessageInput = (event: Event) => {
      handleInputChange((event.target as HTMLTextAreaElement).value);
    };

    const onSendNow = () => {
      confirmModal.close();
      if (selectedPartners.value === null) throw new Error('Partner is not selected');

      sendRequestToPartners({
        expire_in: expires.value,
        message: message.value,
        quiz_ids: [selectedPartners.value.id],
        email: email.value,
        reminder: reminder.value,
      }).then(() => {
        successModal.open();
        notification.success({
          message: 'Request Sent!',
          description: 'You have successfully sent a new request.',
        });
      })
        .catch((error) => {
          let errorStr = error.response.data.message || 'Something wen wrong, please check your mail config' as string;

          notification.error({
            message: 'Error!',
            description: errorStr,
            duration: 8,
          });
        });
    };

    const closeSuccessModalAndResetValues = () => {
      email.value = '';
      reminder.value = null;
      expires.value = 24;
      successModal.close();
    };

    const openSendNowModal = () => {
      if (selectedPartners.value === null) {
        notification.error({
          message: 'Error!',
          description: 'To partner is required.',
          duration: 4,
        });
      }

      if (email.value === '') {
        notification.error({
          message: 'Error!',
          description: 'Email is required.',
          duration: 4,
        });
      }

      if (selectedPartners.value !== null && email.value !== '') {
        confirmModal.open();
      }
    };

    return {
      selectedPartners,
      partnersOptions,
      partnersEMSOptions,
      message,
      reminder,
      expires,
      handleInputChange,
      onMessageInput,
      onSendNow,
      confirmModal,
      successModal,
      email,
      closeSuccessModalAndResetValues,
      openSendNowModal,
      toggleSidebar,
      selectedEpxEms,
      epxEmsRadioOptions
    };
  },
});
